import { ConditionalLink } from "components/conditionalLink";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Button } from "components/DesignSystem/Button/Button";
import { HighlightSearchTerm } from "components/DesignSystem/HighlightText";
import { Cart } from "components/icons/Cart";
import { ProductCard } from "components/ProductCard/ProductCard";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useQuery } from "hooks/useQuery";
import { useLocation, useRouteMatch } from "react-router-dom";
import { Product, useGetCartQuery } from "store/apis/products";
import { parse } from "qs";
import ToolTip from "components/design/toolTip";
import { CartAdded } from "components/icons/CartAdded";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useDispatch } from "react-redux";
import { openFloatingChat, setToOpenChatId } from "store/slices/chat";
import { chatApi, useCreateMessageDraftMutation } from "store/apis/chat";
import { US_BOOKKEEPING_ONE_TIME } from "types/Models/services";

export const ProductTypeCard = ({ product }: { product: Product }) => {
  const query = useQuery();
  const search = query.get("search") || "";
  const { link } = useConstructInternalLink();
  const { url } = useRouteMatch();
  const location = useLocation();
  const parsedSearch = parse(location.search, { ignoreQueryPrefix: true });
  const { isAdmin } = useRoleBasedView();
  const {
    uuid: groupId,
    entities,
    secondary_channel_url,
  } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const dispatch = useDispatch();
  const [createUpdateDraft, { isLoading }] = useCreateMessageDraftMutation();

  const isBookkeepingOneTime =
    product.base_task_key === US_BOOKKEEPING_ONE_TIME;

  const { data: cart } = useGetCartQuery(
    { groupId: groupId!, entityId },
    { skip: !groupId || !entityId }
  );

  const inCartProducts = cart?.cart_items.filter(
    (item) => item.product_details.product_id === product.uuid
  );

  const combinedStateSeasonInCart = inCartProducts
    ?.map((item) => {
      const stateName = item.ra_state_name || item.product_details.state?.name;
      const season = item.product_details.season;
      return stateName && season
        ? `${stateName}-${season}`
        : stateName || season;
    })
    .filter(Boolean)
    .join(", ");

  const currentEntityName = entities.find(
    (item) => item.uuid === entityId
  )?.name;

  const onViewDetailHandler = async () => {
    if (secondary_channel_url) {
      const messageData = await createUpdateDraft({
        channelUrl: secondary_channel_url,
        message: `I would like to get a bookkeeping catchup done. Can you help me with the next step`,
      });

      if (messageData.data) {
        await dispatch(
          chatApi.util.upsertQueryData(
            "getDraftMessage",
            { channelUrl: secondary_channel_url },
            messageData.data
          )
        );
      }
    }

    dispatch(setToOpenChatId(secondary_channel_url));
    dispatch(openFloatingChat());
  };

  const addToCartAction = (
    <div className="t-flex t-justify-between">
      <ConditionalLink
        to={link(`${url}/add-to-cart/${product.uuid}`, {
          moreQuery: parsedSearch as {},
        })}
      >
        <Button size="small">
          <div className="t-flex t-gap-2 t-items-center">
            <Cart /> Add to cart
          </div>
        </Button>
      </ConditionalLink>
      {isAdmin && inCartProducts?.length ? (
        <ToolTip
          text={`Added for: ${combinedStateSeasonInCart || currentEntityName}`}
        >
          <div className="t-text-caption t-text-text-30 t-flex t-items-center t-gap-1 -t-mb-6 t-mr-1">
            <CartAdded />
            <div>Added to cart</div>
          </div>
        </ToolTip>
      ) : null}
    </div>
  );

  const viewDetailAction = (
    <Button size="small" isLoading={isLoading} onClick={onViewDetailHandler}>
      View detail
    </Button>
  );

  const action =
    isBookkeepingOneTime && !isAdmin ? viewDetailAction : addToCartAction;

  return (
    <ProductCard
      key={product.uuid}
      title={<HighlightSearchTerm text={product.title} searchTerm={search} />}
      description={product.display_text}
      price={
        !isNaN(Number(product.approx_price)) ? (
          <p className="t-m-0 t-text-body-sm t-text-text-100">
            <AmountSuperScript amount={Number(product.approx_price)} />
            {product.billing_period && (
              <span className="t-text-body-sm t-text-text-30">
                /{product.billing_period}
              </span>
            )}
          </p>
        ) : (
          <span />
        )
      }
      action={action}
    />
  );
};
